import React, { useState } from 'react'
import { BarChart3, Users, Link2, Coins, Trash2, Settings, MessageSquareText, Gauge, FileSpreadsheet } from 'lucide-react'

export const ADMIN_SECTIONS = [
  { value: 'analytics', label: 'Analytics', icon: BarChart3 },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'reports', label: 'Reports', icon: FileSpreadsheet },
  { value: 'login-link', label: 'Login link', icon: Link2 },
  { value: 'configurations', label: 'Configurations', icon: Settings },
  { value: 'prompt-editor', label: 'Prompt Editor', icon: MessageSquareText },
  { value: 'credit-system', label: 'Credit System', icon: Gauge },
  { value: 'token-usage', label: 'Token & cost', icon: Coins },
  { value: 'deleted-sessions', label: 'Deleted Sessions', icon: Trash2 },
] as const

export type AdminSectionValue = (typeof ADMIN_SECTIONS)[number]['value']

interface AdminSidebarProps {
  open: boolean
  onToggle: () => void
  activeSection: AdminSectionValue
  onSelectSection: (section: AdminSectionValue) => void
  onBackToChat: () => void
  userDisplayName?: string
  userInitials?: string
  onOpenProfile?: () => void
  onOpenTC?: () => void
}

function SectionRow({
  label,
  icon: Icon,
  active,
  onClick,
  collapsed,
}: {
  label: string
  icon: React.ComponentType<{ size?: number }>
  active: boolean
  onClick: () => void
  collapsed?: boolean
}) {
  const [hov, setHov] = useState(false)
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      title={collapsed ? label : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : undefined,
        gap: 8,
        padding: collapsed ? '10px' : '7px 9px',
        borderRadius: 7,
        cursor: 'pointer',
        marginBottom: 1,
        background: active ? 'rgba(255,255,255,.1)' : hov ? 'rgba(255,255,255,.07)' : 'transparent',
        border: active ? '1px solid rgba(255,255,255,.12)' : '1px solid transparent',
        transition: 'all .18s',
      }}
    >
      {!collapsed && <span style={{ fontSize: 11, flexShrink: 0, opacity: 0.55, color: 'var(--white)' }}>·</span>}
      <Icon size={14} style={{ color: active ? 'var(--white)' : 'var(--on-navy-soft)', flexShrink: 0 }} />
      {!collapsed && (
        <span
          style={{
            flex: 1,
            fontSize: 12,
            color: active ? 'var(--white)' : 'var(--on-navy-soft)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: active ? 500 : 400,
          }}
        >
          {label}
        </span>
      )}
    </div>
  )
}

function SbRow({
  children,
  onClick,
  collapsed,
  title,
}: {
  children: React.ReactNode
  onClick: () => void
  collapsed?: boolean
  title?: string
}) {
  const [hov, setHov] = useState(false)
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      title={title}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : 8,
        width: '100%',
        boxSizing: 'border-box',
        padding: collapsed ? '10px 6px' : '8px 9px',
        borderRadius: 7,
        cursor: 'pointer',
        marginBottom: 5,
        background: hov ? 'rgba(255,255,255,.07)' : 'transparent',
        transition: 'background .18s',
      }}
    >
      {children}
    </div>
  )
}

export default function AdminSidebar({
  open,
  onToggle,
  activeSection,
  onSelectSection,
  onBackToChat,
  userDisplayName = 'User',
  userInitials = 'U',
  onOpenProfile,
  onOpenTC,
}: AdminSidebarProps) {
  const collapsed = !open
  const sidebarWidth = open ? 'var(--sb-w)' : 'var(--sb-w-collapsed)'
  return (
    <>
      <div
        style={{
          width: sidebarWidth,
          height: '100vh',
          background: 'var(--navy)',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 100,
          transition: 'width .3s cubic-bezier(.16,1,.3,1)',
          boxShadow: '4px 0 24px rgba(0,0,0,.18)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: collapsed ? '12px 10px' : '18px 14px 14px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 10, marginBottom: collapsed ? 10 : 14, justifyContent: collapsed ? 'center' : undefined }}>
            <div
              style={{
                width: 33,
                height: 33,
                background: 'linear-gradient(135deg,var(--gold),#E8C96A)',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Playfair Display',serif",
                fontSize: 16,
                fontWeight: 600,
                color: 'var(--navy)',
                flexShrink: 0,
              }}
            >
              A
            </div>
            {!collapsed && (
              <div>
                <span
                  style={{
                    fontFamily: "'Playfair Display',serif",
                    fontSize: 15,
                    fontWeight: 500,
                    color: 'var(--white)',
                    display: 'block',
                    lineHeight: 1,
                  }}
                >
                  Athena{' '}
                  <span
                    style={{
                      display: 'inline-flex',
                      background: 'rgba(201,168,76,.15)',
                      border: '1px solid rgba(201,168,76,.3)',
                      borderRadius: 100,
                      padding: '2px 7px',
                      fontSize: 8.5,
                      fontWeight: 600,
                      color: 'var(--gold)',
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    Beta
                  </span>
                </span>
                <span
                  style={{
                    fontSize: 8.5,
                    color: 'rgba(201,168,76,.8)',
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    display: 'block',
                    marginTop: 2,
                  }}
                >
                  Strategy · Intelligence
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onBackToChat}
            type="button"
            title="Back to Chat"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              padding: collapsed ? '8px' : '8px 12px',
              background: 'rgba(255,255,255,.08)',
              border: '1px solid rgba(255,255,255,.12)',
              borderRadius: 9,
              color: 'var(--white)',
              fontSize: collapsed ? 0 : 12.5,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            {!collapsed && ' Back to Chat'}
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: collapsed ? '10px 0' : '10px 8px' }}>
          {!collapsed && (
            <div
              style={{
                fontSize: 9.5,
                fontWeight: 500,
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                color: 'var(--on-navy-dim)',
                padding: '6px 8px 4px',
              }}
            >
              Sections
            </div>
          )}
          {ADMIN_SECTIONS.map(({ value, label, icon }) => (
            <SectionRow
              key={value}
              label={label}
              icon={icon}
              active={activeSection === value}
              onClick={() => onSelectSection(value)}
              collapsed={collapsed}
            />
          ))}
        </div>
        <div
          style={{
            padding: collapsed ? '10px 6px' : '10px 12px',
            borderTop: '1px solid rgba(255,255,255,.07)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: collapsed ? 'center' : 'stretch',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {onOpenProfile && (
            <SbRow
              onClick={onOpenProfile}
              collapsed={collapsed}
              title={collapsed ? `${userDisplayName} — Profile` : undefined}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,.12)',
                  border: '1.5px solid rgba(255,255,255,.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9.5,
                  fontWeight: 500,
                  color: 'var(--white)',
                  flexShrink: 0,
                }}
              >
                {userInitials}
              </div>
              {!collapsed && (
                <>
                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <span
                      style={{
                        fontSize: 11.5,
                        fontWeight: 500,
                        color: 'var(--white)',
                        display: 'block',
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {userDisplayName}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        color: 'var(--on-navy-faint)',
                        display: 'block',
                        marginTop: 2,
                        lineHeight: 1.2,
                      }}
                    >
                      Profile
                    </span>
                  </div>
                  <span
                    style={{
                      color: 'var(--on-navy-dim)',
                      fontSize: 13,
                      lineHeight: 1,
                      flexShrink: 0,
                      alignSelf: 'center',
                      paddingLeft: 4,
                    }}
                  >
                    ⋯
                  </span>
                </>
              )}
            </SbRow>
          )}
          {!collapsed && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '5px 9px 2px',
              }}
            >
              {onOpenTC ? (
                <span
                  onClick={onOpenTC}
                  onKeyDown={(e) => e.key === 'Enter' && onOpenTC()}
                  role="button"
                  tabIndex={0}
                  style={{ fontSize: 10.5, color: 'var(--on-navy-dim)', cursor: 'pointer' }}
                >
                  Terms of Use
                </span>
              ) : (
                <span style={{ fontSize: 10.5, color: 'var(--on-navy-dim)' }}>Terms</span>
              )}
              <span style={{ color: 'var(--on-navy-dot)', fontSize: 10 }}>·</span>
              <span style={{ fontSize: 10.5, color: 'var(--gold-muted)' }}>Beta v0.1</span>
            </div>
          )}
        </div>
      </div>
      <div
        onClick={onToggle}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        role="button"
        tabIndex={0}
        aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
        style={{
          position: 'fixed',
          left: open ? 'var(--sb-w)' : 'var(--sb-w-collapsed)',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 22,
          height: 56,
          background: 'var(--navy)',
          borderRadius: '0 10px 10px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 101,
          transition: 'left .3s cubic-bezier(.16,1,.3,1)',
          boxShadow: '2px 0 12px rgba(0,0,0,.15)',
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--on-navy-soft)"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          {open ? (
            <polyline points="15 18 9 12 15 6" />
          ) : (
            <polyline points="9 18 15 12 9 6" />
          )}
        </svg>
      </div>
    </>
  )
}
